import { utcToZonedTime } from 'date-fns-tz'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Banner,
  Button,
  DateHelper,
  DatePicker,
  ErrorMessage,
  Label,
  Layout,
  LoadingDots,
  Text,
  TextField,
} from '@loadsmart/loadsmart-ui'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import {
  type FetchLocationOptions,
  type Location as ReactLocation,
} from '@loadsmart/react-location-select'

import { type Location } from 'core/domain/Location'
import { formatLocationToOption } from 'core/presenters/locationPresenter'
import TruckInfoAndLocationIcon from 'core/ui/assets/truck-info.svg'
import { LocationSelect } from 'core/ui/components/location/LocationSelect'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { dateTimeToMilitaryTime, getTimeFromMilitaryTime } from 'core/utils/militaryTime'

import { analytics } from 'legacy/utils/analytics'
import { addHours } from 'legacy/utils/date/addHours'
import { addMinutes } from 'legacy/utils/date/addMinutes'
import { formatDate } from 'legacy/utils/date/formatDate'
import { isMilitaryTime } from 'legacy/utils/date/isMilitaryTime'
import { startOfDay } from 'legacy/utils/date/startOfDay'

import { type EmptyLocation, isDateBeforePickupTime } from '../../../domain/shipment/EmptyLocation'
import { type Shipment } from '../../../domain/shipment/Shipment'
import { type EmptyLocationPayload } from '../../../infra/shipment/ShipmentsRepository'
import { getPickupDateTime } from '../../../presenters/truckInfoPresenter'
import { useTruckInformation } from '../../hooks/useTruckInformation'
import { ContentWrapper, ImageWrapper, Wrapper } from './Step.styles'
import { type StepProps } from './Step.types'
import { ColumnWrapper, DatePickerWrapper, RowWrapper } from './TruckInfoAndLocationStep.styles'

const CUSTOM_GOOGLE_MAPS_CONFIG: FetchLocationOptions = {
  types: ['geocode'],
  restrictions: { country: ['us', 'ca', 'mx'] },
}

const TODAY = DateHelper()

type EmptyLocationPayloadProps = {
  date: string
  location: Location
  shipmentId: string
  time: string
}

// TODO: Refactor me in the future
function emptyLocationPayload(emptyLocation: EmptyLocationPayloadProps): EmptyLocationPayload {
  const { hours, minutes } = getTimeFromMilitaryTime(emptyLocation.time)

  // strip timezone data before sending it to the backend
  const availabilityDate = formatDate(
    addMinutes(addHours(startOfDay(new Date(emptyLocation.date)), hours), minutes),
    `yyyy-MM-dd'T'HH:mm:ss.SSS`
  )

  return {
    shipmentId: emptyLocation.shipmentId,
    availabilityDate,
    city: emptyLocation.location.city ?? '',
    state: emptyLocation.location.state ?? '',
  }
}

function toLocalTime(date: Date, timezone: string): Date {
  return utcToZonedTime(date, timezone)
}

function inputLocalTime(date: string, time: string): Date {
  const hours = time.substring(0, 2)
  const minutes = time.substring(2, 4)
  const dateTime = addMinutes(addHours(startOfDay(new Date(date)), hours), minutes)

  return dateTime
}

export function TruckInfoAndLocationStep({
  loadOffer,
  shipment,
  onComplete,
  onMarkAsComplete,
}: StepProps) {
  const {
    emptyLocation,
    isUpdatingTruckInfo,
    isSavingEmptyLocation,
    savedEmptyLocation,
    updateTruckInfoByShipmentId,
    saveEmptyLocationByShipmentId,
    fetchEmptyLocationByShipmentId,
  } = useTruckInformation()

  const abortController = useAbortController()

  const pickupDateTime = loadOffer.stops[0].windowStart
  const timezone = loadOffer.stops[0].timezone
  const shipmentId = loadOffer.refNumber

  const [date, setDate] = useState<string>(getPickupDateTime(pickupDateTime))
  const [time, setTime] = useState<string>('')
  const [location, setLocation] = useState<Location>()
  const [tractorNumber, setTractorNumber] = useState<string>('')
  const [trailerNumber, setTrailerNumber] = useState<string>('')
  const [hasDateTimeError, setHasDateTimeError] = useState<boolean>(false)

  const isSubmittingTruckInfo = isSavingEmptyLocation || isUpdatingTruckInfo
  const isSubmitDisabled = !date || time.length < 4 || hasDateTimeError || !location?.placeId

  const handleDateChange = useCallback((event: EventLike<string | null>) => {
    setDate(event.target.value!)
  }, [])

  const handleTimeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    if (isMilitaryTime(value)) {
      setTime(event.target.value)
    }
  }, [])

  const handleLocationChange = useCallback((newLocation?: ReactLocation) => {
    setLocation({
      ...newLocation,
      placeId: newLocation?.place_id,
    })
  }, [])

  const handleTractorNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTractorNumber(event.target.value)
  }, [])

  const handleTrailerNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTrailerNumber(event.target.value)
  }, [])

  const handleSubmitClick = useCallback(async () => {
    if (isSubmittingTruckInfo) return
    if (!location) return

    try {
      if (tractorNumber !== shipment.tractorNumber || trailerNumber !== shipment.trailerNumber) {
        const truckInfo = { shipmentId, tractorNumber, trailerNumber }
        await updateTruckInfoByShipmentId(truckInfo)
        analytics.track('Load Setup / Truck Info And Location / Update truck info', truckInfo)
      }

      const emptyLocation = emptyLocationPayload({
        shipmentId,
        time,
        date,
        location,
      })

      await saveEmptyLocationByShipmentId(emptyLocation)

      analytics.track('Load Setup / Truck Info And Location / Add empty location', emptyLocation)
      analytics.track('Load Setup / Truck Info And Location / Completed')
      toast.success('Truck info and location saved correctly')
      onComplete()
    } catch {
      toast.error('Error saving truck info and location')
    }
  }, [
    isSubmittingTruckInfo,
    tractorNumber,
    trailerNumber,
    shipmentId,
    shipment,
    time,
    date,
    location,
    saveEmptyLocationByShipmentId,
    onComplete,
    updateTruckInfoByShipmentId,
  ])

  const populateEmptyLocation = useCallback(
    (emptyLocation?: EmptyLocation) => {
      if (!emptyLocation) return

      if (emptyLocation.timezone) {
        const localTime = toLocalTime(emptyLocation.availabilityDate, emptyLocation.timezone)
        setTime(dateTimeToMilitaryTime(localTime))
        setDate(getPickupDateTime(localTime))
      }

      setLocation({
        address: `${emptyLocation.city}, ${emptyLocation.state}`,
      })

      onMarkAsComplete()
    },
    [onMarkAsComplete]
  )

  const populateTruckInfo = useCallback((shipment: Shipment) => {
    setTractorNumber(shipment.tractorNumber ?? '')
    setTrailerNumber(shipment.trailerNumber ?? '')
  }, [])

  useEffect(() => {
    if (date && time.length === 4) {
      const pickupTime = toLocalTime(pickupDateTime, timezone)
      const inputTime = inputLocalTime(date, time)
      const hasError = isDateBeforePickupTime(inputTime, pickupTime!)

      setHasDateTimeError(hasError)
    }
  }, [date, time, pickupDateTime, timezone])

  useEffect(() => {
    fetchEmptyLocationByShipmentId(shipmentId, abortController.signal)
  }, [shipmentId, abortController, fetchEmptyLocationByShipmentId])

  useEffect(() => {
    populateEmptyLocation(emptyLocation)
  }, [emptyLocation, populateEmptyLocation])

  useEffect(() => {
    populateTruckInfo(shipment)
  }, [shipment, populateTruckInfo])

  return (
    <Layout.Stack>
      <Wrapper>
        <ImageWrapper>
          <Layout.Stack>
            <Text as="p">Let us know about your truck's info and MT location.</Text>
            <img src={TruckInfoAndLocationIcon} alt="Truck info and location" />
          </Layout.Stack>
        </ImageWrapper>
        <ContentWrapper>
          <Layout.Stack space="s">
            <RowWrapper>
              <ColumnWrapper>
                <Label htmlFor="date">Date</Label>
                <DatePickerWrapper>
                  <DatePicker
                    // This constraint doesn't allow to select days before
                    constraints={[[null, TODAY.subtract('day', 1).toString()]]}
                    id="date"
                    name="date"
                    onChange={handleDateChange}
                    value={date}
                  />
                </DatePickerWrapper>
              </ColumnWrapper>
              <ColumnWrapper>
                <Label htmlFor="time">Military time</Label>
                <TextField
                  id="time"
                  maxLength={4}
                  name="time"
                  onChange={handleTimeChange}
                  placeholder="0000"
                  type="number"
                  value={time}
                />
                {hasDateTimeError && <ErrorMessage>Date must be before pickup time</ErrorMessage>}
              </ColumnWrapper>
            </RowWrapper>
            <ColumnWrapper>
              <Label htmlFor="location">Location</Label>
              <LocationSelect
                id="location"
                name="location"
                placeholder="City, state or zip"
                value={location}
                mapLocationToValue={location => location}
                mapValueToOption={formatLocationToOption}
                onChange={handleLocationChange}
                config={CUSTOM_GOOGLE_MAPS_CONFIG}
              />
            </ColumnWrapper>
            <RowWrapper>
              <ColumnWrapper>
                <Label htmlFor="tractor">Tractor (Optional)</Label>
                <TextField
                  id="tractor"
                  name="tractor"
                  placeholder="Number"
                  maxLength={10}
                  value={tractorNumber}
                  onChange={handleTractorNumberChange}
                />
              </ColumnWrapper>
              <ColumnWrapper>
                <Label htmlFor="trailer">Trailer (Optional)</Label>
                <TextField
                  id="trailer"
                  name="trailer"
                  placeholder="Number"
                  maxLength={10}
                  value={trailerNumber}
                  onChange={handleTrailerNumberChange}
                />
              </ColumnWrapper>
            </RowWrapper>
            <Button variant="primary" onClick={handleSubmitClick} disabled={isSubmitDisabled}>
              {isSubmittingTruckInfo ? <LoadingDots /> : 'Submit'}
            </Button>
          </Layout.Stack>
        </ContentWrapper>
      </Wrapper>
      {savedEmptyLocation && (
        <Banner title="Truck info and location saved successfully" variant="success" />
      )}
    </Layout.Stack>
  )
}
